/**
 * Giris ekrani.
 *
 * ── HATA MESAJI SUNUCUDAN ────────────────────────────────────────────────────
 * "E-posta veya parola hatalı" metnini istemci UYDURMAZ; sunucu doner. Sebebi:
 * hangisinin yanlis oldugunu soylemek hesap sayimina (account enumeration)
 * kapi acar ve bu karar tek yerde verilmeli.
 *
 * ── GOOGLE BUTONU KOSULLU ────────────────────────────────────────────────────
 * `/auth/config` Google'in yapilandirilip yapilandirilmadigini soyler. Calismayan
 * bir buton gostermek, spec §4.13'un ("her buton calisir") ihlalidir.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api, ApiRequestError } from '@/lib/api';
import { Button, Checkbox, Field, Input } from '@/components/ui';
import { useI18n } from '@/lib/i18n';
import { useToast } from '@/lib/toast';

interface AuthConfig {
  googleEnabled: boolean;
  googleStartUrl: string | null;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleStartUrl, setGoogleStartUrl] = useState<string | null>(null);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(() => (location.state as { twoFactorToken?: string } | null)?.twoFactorToken ?? null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  useEffect(() => {
    void api
      .get<AuthConfig>('/auth/config')
      .then((cfg) => {
        setGoogleEnabled(cfg.googleEnabled);
        setGoogleStartUrl(cfg.googleStartUrl);
      })
      .catch(() => setGoogleEnabled(false));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFields({});
    setNeedsVerify(false);
    setBusy(true);

    try {
      const outcome = await login(email.trim(), password, remember, twoFactorToken ?? undefined, twoFactorCode || undefined);
      if (outcome.requiresTwoFactor) {
        setTwoFactorToken(outcome.twoFactorToken);
        setPassword('');
        return;
      }
      const me = outcome.me;
      const from = (location.state as { from?: string } | null)?.from;
      // Yonetim yetkili hesabin evi konsoldur: business hesabi girer girmez
      // yonetim yuzeyine iner. Derin baglanti (from) her zaman kazanir.
      navigate(from ?? (me.nav.includes('admin') ? '/yonetim' : '/'), { replace: true });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
        setFields(err.fields ?? {});
        if (err.code === 'EMAIL_NOT_VERIFIED') setNeedsVerify(true);
      } else {
        setError(t('auth.serverUnavailable'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification() {
    try {
      await api.post('/auth/resend-verification', { email: email.trim() });
      setError(null);
      setNeedsVerify(false);
      setFields({});
      toast.success(t('auth.resendSuccess'));
    } catch {
      setError(t('auth.resendFail'));
    }
  }

  return (
    <>
      <div>
        <h1 className="auth__title">{t('auth.signIn')}</h1>
        <p className="auth__sub">{t('auth.signInSubtitle')}</p>
      </div>

      <form className="auth__form" onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="auth__error" role="alert">
            {error}
            {needsVerify && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => void resendVerification()}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  {t('auth.resendVerification')}
                </button>
              </>
            )}
          </div>
        )}

        {!twoFactorToken && <Field label={t('auth.email')} required error={fields['email']}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              required
              invalid={invalid}
              aria-describedby={describedBy}
              placeholder="name@company.com"
            />
          )}
        </Field>}

        {!twoFactorToken && <Field label={t('auth.password')} required error={fields['password']}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              invalid={invalid}
              aria-describedby={describedBy}
            />
          )}
        </Field>}

        {twoFactorToken && (
          <Field label="Doğrulama kodu" required hint="Kimlik doğrulama uygulamandaki altı haneli kodu veya kurtarma kodlarından birini gir.">
            {({ id }) => (
              <Input id={id} value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value)} autoComplete="one-time-code" autoFocus required />
            )}
          </Field>
        )}

        {!twoFactorToken && <div className="auth__links">
          <Checkbox
            label={t('auth.rememberMe')}
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <Link to="/parola-sifirla">{t('auth.forgotPassword')}</Link>
        </div>}

        <Button type="submit" variant="primary" size="lg" loading={busy}>
          {twoFactorToken ? 'Doğrula ve devam et' : t('auth.signIn')}
        </Button>

        {twoFactorToken && (
          <Button type="button" variant="ghost" onClick={() => { setTwoFactorToken(null); setTwoFactorCode(''); }}>
            Farklı hesapla giriş yap
          </Button>
        )}

        {!twoFactorToken && googleEnabled && googleStartUrl && (
          <>
            <div className="auth__divider">{t('auth.or')}</div>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => {
                // Tam sayfa yonlendirme: OAuth akisi tarayicinin kendi
                // gezinmesiyle yurur, fetch ile degil.
                const target = new URL(googleStartUrl);
                const current = new URLSearchParams(window.location.search);
                for (const [from, to] of [['catalog', 'catalog'], ['product', 'product'], ['source', 'source']] as const) {
                  const value = current.get(from);
                  if (value) target.searchParams.set(to, value);
                }
                window.location.href = target.toString();
              }}
            >
              {t('auth.googleSignIn')}
            </Button>
          </>
        )}
      </form>

      <p className="small muted" style={{ textAlign: 'center' }}>
        {t('auth.noAccount')} <Link to="/kayit">{t('auth.register')}</Link>
      </p>
    </>
  );
}
